import * as React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {useState } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {AuthContext} from '../App'
import { BrandLogo } from '../layout/components/BrandLogo';
import axios from "axios";
import config from "../config";

const apiUrl = config.apiGateway.URL + config.apiGateway.apiPrefix;

const formatStat = (value) => {
    const number = Number(value) || 0;

    if (number >= 1000000) {
        return `${(number / 1000000).toFixed(number >= 10000000 ? 0 : 1)}M`;
    }

    if (number >= 1000) {
        return `${(number / 1000).toFixed(number >= 10000 ? 0 : 1)}k`;
    }

    return number.toLocaleString();
};

const useAuth = () => {
    return React.useContext(AuthContext);
}

export function Login({show, onClose}) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [loginInvalid, setLoginInvalid] = useState(false);
    const [platformStats, setPlatformStats] = useState({
        minimumDataCompanies: 20,
        operatingCompanyDataPoints: 0,
        stockHistoryYears: 5,
    });
    let location = useLocation();
    let navigate = useNavigate();

    const { onLogin, onLoad } = useAuth();

    const handleLogin = (event) => {
        event?.preventDefault();
        setLoginInvalid(false);
        setLoading(true);
        onLogin(email, password, onFailure);
    }

    const onFailure =()=> {
        setLoading(false);
        setLoginInvalid(true);
        toast.error("Login failed", {})
    }

    React.useEffect(() => {
        onLoad();
        axios.get(`${apiUrl}/public/platform-stats`, {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        }).then((response) => {
            setPlatformStats({
                minimumDataCompanies: response.data?.minimum_data_companies ?? 20,
                operatingCompanyDataPoints: response.data?.operating_company_data_points ?? 0,
                stockHistoryYears: response.data?.stock_history_years ?? 5,
            });
        }).catch(() => {
            setPlatformStats((current) => current);
        });
    }, []);

    return(
        <>
            <ToastContainer/>
            <main className="min-h-screen w-full bg-[#f4f8f6] text-[#111827]">
                <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col px-5 py-6 lg:flex-row lg:items-stretch lg:gap-8 lg:px-8">
                    <section className="flex flex-1 flex-col justify-between rounded-lg bg-[#052e24] px-6 py-7 text-white shadow-[0_24px_80px_rgba(15,23,42,0.16)] lg:px-10 lg:py-10">
                        <div className="flex items-center justify-between">
                            <BrandLogo to="/" inverse/>
                            <span className="rounded-full border border-white/20 px-3 py-1 text-xs font-medium text-white/80">
                                Investor intelligence
                            </span>
                        </div>

                        <div className="my-14 max-w-xl lg:my-0 lg:self-center">
                            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#86efac]">
                                Financial and sustainability research
                            </p>
                            <h1 className="text-4xl font-semibold leading-tight text-white lg:text-5xl">
                                Sign in to your company intelligence dashboard
                            </h1>
                            <p className="mt-5 max-w-lg text-base leading-7 text-slate-300">
                                Review market data, ESG signals, risk indicators, news flow, and peer-ranked financial metrics in one focused workspace.
                            </p>
                        </div>

                        <div className="grid gap-3 border-t border-white/10 pt-6 text-sm text-slate-300 sm:grid-cols-3">
                            <div>
                                <p className="text-2xl font-semibold text-white">{formatStat(platformStats.minimumDataCompanies)}</p>
                                <p>companies with minimum data</p>
                            </div>
                            <div>
                                <p className="text-2xl font-semibold text-white">{platformStats.stockHistoryYears}y</p>
                                <p>stock history view</p>
                            </div>
                            <div>
                                <p className="text-2xl font-semibold text-white">{formatStat(platformStats.operatingCompanyDataPoints)}</p>
                                <p>supporting your Impact</p>
                            </div>
                        </div>
                    </section>

                    <section className="flex flex-1 items-center justify-center py-8 lg:py-0">
                        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-7 shadow-[0_18px_60px_rgba(15,23,42,0.08)] sm:p-9">
                            <div className="mb-8">
                                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#16a34a]">
                                    Secure access
                                </p>
                                <h2 className="mt-2 text-3xl font-semibold text-slate-950">
                                    Welcome back
                                </h2>
                                <p className="mt-3 text-sm leading-6 text-slate-600">
                                    Enter your credentials to continue to the iVestClub platform.
                                </p>
                            </div>

                            {loginInvalid && (
                                <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                                    Login failed. Please check your email and password.
                                </div>
                            )}

                            <form className="space-y-5" onSubmit={handleLogin}>
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="email">
                                        Email
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="username"
                                        required
                                        className="h-12 w-full rounded-md border border-slate-300 bg-white px-4 text-sm text-slate-950 outline-none transition focus:border-[#16a34a] focus:ring-4 focus:ring-emerald-100"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>

                                <div>
                                    <div className="mb-2 flex items-center justify-between gap-4">
                                        <label className="block text-sm font-medium text-slate-700" htmlFor="password">
                                            Password
                                        </label>
                                        <Link to="/resetpass" className="text-sm font-medium text-[#2563eb] hover:text-[#16a34a]">
                                            Forgot password?
                                        </Link>
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        className="h-12 w-full rounded-md border border-slate-300 bg-white px-4 text-sm text-slate-950 outline-none transition focus:border-[#16a34a] focus:ring-4 focus:ring-emerald-100"
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </div>

                                <button
                                    className="flex h-12 w-full items-center justify-center rounded-md bg-[#16a34a] px-4 text-sm font-semibold text-white transition hover:bg-[#15803d] disabled:cursor-not-allowed disabled:bg-slate-300"
                                    disabled={loading}
                                    type="submit"
                                >
                                    {loading ? 'Signing in...' : 'Sign in'}
                                </button>
                            </form>

                            <div className="mt-7 border-t border-slate-200 pt-6 text-center text-sm text-slate-600">
                                New to iVestClub?{' '}
                                <Link to="/register" className="font-semibold text-[#2563eb] hover:text-[#16a34a]">
                                    Create an account
                                </Link>
                            </div>
                        </div>
                    </section>
                </div>
            </main>
        </>
    )
}
